import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { bandColor, live, hasUnpublishedChanges, adminContentEditLinks, isBrandQuery } from "./seoUtils";
import { Link } from "react-router-dom";
import SeoLivePreview from "@/components/seo/SeoLivePreview";
import SeoFormScore from "@/components/seo/SeoFormScore";
import api from "@/lib/api";

/** Visible tab row — no dropdown. */
const TABS = [
  { id: "Overview", label: "Fix problems" },
  { id: "Meta", label: "Title & description" },
  { id: "Keywords", label: "Keywords" },
  { id: "Images", label: "Images" },
  { id: "Content", label: "Content" },
  { id: "Social", label: "Social" },
  { id: "Schema", label: "Schema" },
  { id: "Links", label: "Links" },
  { id: "Technical", label: "Technical" },
  { id: "History", label: "History" },
];

const SEV = {
  critical: { label: "Must fix", className: "bg-red-500/20 text-red-200 border-red-400/30" },
  high: { label: "Important", className: "bg-orange-500/20 text-orange-100 border-orange-400/30" },
  medium: { label: "Improve", className: "bg-amber-500/15 text-amber-100 border-amber-400/25" },
  low: { label: "Nice to have", className: "bg-white/5 text-white/60 border-white/10" },
};

/** Map analyzer rule_id → where to go and what to highlight. */
function issueGuide(issue, livePageUrl) {
  const id = issue?.rule_id || "";
  const rec = issue?.recommendation || "";
  const map = {
    title_missing: { tab: "Meta", field: "title", action: "Fix title", how: "Write a clear Google title (about 40–60 characters)." },
    title_length: { tab: "Meta", field: "title", action: "Fix title", how: "Shorten or lengthen the title to about 40–60 characters." },
    description_missing: { tab: "Meta", field: "description", action: "Fix description", how: "Write a short summary people will click (about 120–160 characters)." },
    description_length: { tab: "Meta", field: "description", action: "Fix description", how: "Aim for 120–160 characters so Google can show the full line." },
    keyword_missing: { tab: "Keywords", field: "keywords", action: "Add keyword", how: "Add one primary keyword that matches what people search for." },
    alt_missing: { tab: "Images", field: "images", action: "Add ALT text", how: "Describe each image in plain language." },
    og_missing: { tab: "Social", field: "social", action: "Fix social", how: "Add share title and a wide image (about 1200×630)." },
    schema_missing: { tab: "Schema", field: "schema", action: "Add schema", how: "Paste or generate JSON-LD, then Save and Publish." },
    schema_invalid: { tab: "Schema", field: "schema", action: "Fix schema", how: "Fix the JSON syntax, then validate." },
    canonical_missing: { tab: "Meta", field: "canonical", action: "Set canonical", how: "Open “Canonical & indexing” and set the correct URL." },
    canonical_mismatch: { tab: "Meta", field: "canonical", action: "Check canonical", how: "Confirm the canonical URL is intentional." },
    indexability: { tab: "Meta", field: "indexing", action: "Check indexing", how: "Turn Index on unless this page must stay hidden from Google." },
    h1_missing: {
      tab: "Content", field: "content", action: "Open editor",
      how: "Edit the page heading in Brand & Pages / CMS (not in SEO). Use the Open editor button.",
      external: true,
    },
    h1_multiple: {
      tab: "Content", field: "content", action: "Open editor",
      how: "Keep a single H1 in the content editor. Use H2 for sections.",
      external: true,
    },
    thin_content: {
      tab: "Content", field: "content", action: "Open editor",
      how: "Add more useful copy in the admin content editor, save, then re-check.",
      external: true,
    },
    cta_missing: {
      tab: "Content", field: "content", action: "Open editor",
      how: "Add a clear contact / get started CTA in the page content editor.",
      external: true,
    },
    internal_links: {
      tab: "Links", field: "links", action: "Open editor",
      how: "Add links to related pages in the content editor, then re-check.",
      external: true,
    },
    https: { tab: "Technical", field: "technical", action: "See details", how: "Serve this URL over HTTPS on the live site." },
    status_ok: { tab: "Technical", field: "technical", action: "See details", how: "Fix the HTTP error or add a redirect." },
  };
  const g = map[id] || { tab: "Overview", field: null, action: "Review", how: rec || "Open the related tab and fix what the message describes." };
  return { ...g, liveUrl: livePageUrl };
}

function titleWithPhrase(current, phrase) {
  const words = (phrase || "").replace(/\s+/g, " ").trim();
  const base = (current || "").replace(/\s+/g, " ").trim();
  if (!words) return base;
  if (base.toLowerCase().includes(words.toLowerCase())) return base;
  let next = base ? `${words} | ${base}` : words;
  if (next.length <= 60) return next;
  next = `${words} | CAS Media Solutions`;
  if (next.length <= 60) return next;
  return words.slice(0, 60).trim();
}

function fieldRing(active, name) {
  return active === name
    ? "ring-2 ring-amber-400/80 ring-offset-2 ring-offset-[#0b0f14] rounded-lg p-2 -m-2 bg-amber-400/5"
    : "";
}

export default function SEOPageWorkspace({
  page, initialTab, onTabChange, onChange, onSave, onAnalyze, onPublish, onAi, analyzing,
  canPublish, aiOut, onRestore, onSaveImages, onLinkTask, onValidateSchema, onApplyAi,
  gscQuery = "", gscQueries = [], gscStats = null,
}) {
  const [tab, setTab] = useState(initialTab || "Overview");
  const [alts, setAlts] = useState([]);
  const [schemaResult, setSchemaResult] = useState(null);
  const [liveSeo, setLiveSeo] = useState(null);
  const [siteOrigin, setSiteOrigin] = useState("");
  const [highlight, setHighlight] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const highlightTimer = useRef(null);

  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  useEffect(() => {
    setAlts((page?.images?.items || []).map((img) => ({ src: img.src, alt: img.alt || "" })));
    if (!gscQuery) setHighlight(null);
  }, [page?.id, page?.images?.items, gscQuery]);
  useEffect(() => {
    if (!gscQuery) return;
    setTab("Meta");
    setHighlight("title");
    const timer = window.setTimeout(() => {
      document.getElementById("seo-field-title")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [gscQuery, page?.id]);
  useEffect(() => {
    if (!page?.path) return;
    let cancelled = false;
    api.get("/seo/settings").then((r) => {
      if (!cancelled) setSiteOrigin((r.data.production_origin || "").replace(/\/$/, ""));
    }).catch(() => {});
    const path = page.path || "/";
    api
      .get("/public/seo", { params: { path } })
      .then((r) => { if (!cancelled) setLiveSeo(r.data || null); })
      .catch(() => {
        if (!cancelled) setLiveSeo(null);
      });
    return () => { cancelled = true; };
  }, [page?.path, page?.publish_state]);

  const selectTab = (t) => {
    setTab(t);
    onTabChange?.(t);
  };

  if (!page) return <GlassCard className="p-10 text-center text-white/40">Select a page to open the workspace.</GlassCard>;

  const seo = page.seo || {};
  const technical = page.technical || {};
  const social = page.social || {};
  const score = page.seo_score;
  const setSeo = (key, value) => onChange({ ...page, seo: { ...seo, [key]: { ...(seo[key] || {}), configured: value } } });
  const setTech = (key, value) => onChange({ ...page, technical: { ...technical, [key]: { ...(technical[key] || {}), configured: value } } });
  const title = seo.title?.configured || seo.title?.published || "";
  const desc = seo.description?.configured || seo.description?.published || "";
  const rankingQuery = (gscQuery || "").trim();
  const rankingPhrases = (gscQueries.length ? gscQueries : (rankingQuery ? [rankingQuery] : [])).filter(Boolean);
  const topicPhrases = rankingPhrases.filter((phrase) => !isBrandQuery(phrase)).slice(0, 4);
  const brandOnly = rankingPhrases.length > 0 && topicPhrases.length === 0;
  const rankingPos = gscStats?.position || "";
  const canonical = technical.canonical?.configured || page.url || "";
  const og = social.open_graph || {};
  const unpublished = hasUnpublishedChanges(page);
  const livePageUrl = siteOrigin ? `${siteOrigin}${page.path === "/" ? "/" : page.path}` : page.url;
  const issues = page.issues || [];
  const issueCount = issues.length;
  const adminLinks = adminContentEditLinks(page);

  const goFix = (issue) => {
    const guide = issueGuide(issue, livePageUrl);
    selectTab(guide.tab);
    if (guide.field) {
      setHighlight(guide.field);
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlight(null), 8000);
      requestAnimationFrame(() => {
        document.getElementById(`seo-field-${guide.field}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  const runAi = async (action) => {
    if (!onAi) return;
    setAiBusy(true);
    try {
      await onAi(action);
    } finally {
      setAiBusy(false);
    }
  };

  const sortedIssues = [...issues].sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    return (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
  });
  const topIssue = sortedIssues[0];

  return (
    <div className="space-y-4 w-full max-w-[1600px]">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-white break-words">{page.name}</h2>
          <p className="text-xs font-mono text-white/50 break-all">{page.url}</p>
          {unpublished && <p className="text-xs text-amber-300 mt-1">Unpublished edits — Save, then Publish when ready.</p>}
          {livePageUrl && (
            <a href={livePageUrl} target="_blank" rel="noreferrer" className="text-xs text-cyan-300/80 hover:text-cyan-200 mt-1 inline-block">Open live page ↗</a>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-sm font-semibold" style={{ color: bandColor(score) }} title="SEO score">{score ?? "—"}</div>
          <Button variant="ghost" onClick={onAnalyze} disabled={analyzing} className="text-white/80">{analyzing ? "Checking…" : "Check page"}</Button>
          <Button onClick={onSave} className="bg-white/10 text-white">Save</Button>
          <Button onClick={onPublish} disabled={!canPublish} className="btn-gradient text-white border-0">Publish</Button>
        </div>
      </div>

      {issueCount > 0 && tab !== "Overview" && (
        <button
          type="button"
          onClick={() => selectTab("Overview")}
          className="w-full text-left rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2 hover:bg-amber-400/15"
        >
          <div className="min-w-0">
            <p className="text-sm text-amber-50 font-medium">
              {issueCount} problem{issueCount === 1 ? "" : "s"} to fix
            </p>
            <p className="text-xs text-amber-100/70 mt-0.5 line-clamp-2 sm:line-clamp-1">{topIssue?.message}</p>
          </div>
          <span className="text-sm text-amber-100 shrink-0 px-3 py-1.5 rounded-lg bg-amber-400/20 border border-amber-400/30 self-start">
            Open Fix problems →
          </span>
        </button>
      )}

      <p className="text-xs text-white/45">
        {issueCount > 0
          ? "Use the yellow Fix problems tab (badge) — each card has a Fix button that jumps to the field."
          : "Looks good. Edit title & description anytime, then Save → Publish."}
      </p>
      <SeoFormScore page={page} />

      <div className="w-full border-b border-white/5">
        <div className="flex gap-1.5 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-thin [scrollbar-width:thin]">
          {TABS.map((t) => {
            const badge = t.id === "Overview" && issueCount > 0 ? issueCount : null;
            const isFix = t.id === "Overview";
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => selectTab(t.id)}
                className={`px-3 h-9 rounded-lg text-sm inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
                  tab === t.id
                    ? isFix
                      ? "bg-amber-400/20 text-amber-50 ring-1 ring-amber-400/40"
                      : "bg-white/12 text-white"
                    : isFix && issueCount > 0
                      ? "text-amber-100/80 bg-amber-400/5 hover:bg-amber-400/10"
                      : "text-white/50 hover:text-white hover:bg-white/5"
                }`}
              >
                {t.label}
                {badge != null && (
                  <span className="min-w-[1.25rem] h-5 px-1 rounded-md bg-amber-400/30 text-amber-50 text-[11px] flex items-center justify-center font-medium">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "Overview" && (
        <div className="space-y-4">
          {sortedIssues.length === 0 ? (
            <GlassCard className="p-6 text-center space-y-2">
              <p className="text-white text-sm">No problems on the last check.</p>
              <p className="text-xs text-white/45">You can still tweak the Google title under Title &amp; description.</p>
              <Button variant="ghost" className="text-white/70" onClick={() => selectTab("Meta")}>Edit title &amp; description</Button>
            </GlassCard>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-white/70">Tap <strong className="text-white font-medium">Fix</strong> — we open the right tab and highlight the field.</p>
              {sortedIssues.map((issue, i) => {
                const guide = issueGuide(issue, livePageUrl);
                const sev = SEV[issue.severity] || SEV.medium;
                const primaryAdmin = guide.external ? adminLinks[0] : null;
                return (
                  <GlassCard key={`${issue.rule_id}-${i}`} className="p-4 border border-white/10">
                    <div className="flex flex-wrap items-start gap-3">
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${sev.className}`}>{sev.label}</span>
                      <div className="flex-1 min-w-0 basis-full sm:basis-auto sm:min-w-[200px] space-y-1">
                        <p className="text-sm text-white">{issue.message}</p>
                        <p className="text-xs text-white/50 leading-relaxed">{guide.how}</p>
                        {primaryAdmin && (
                          <Link to={primaryAdmin.to} className="text-xs text-cyan-300 hover:underline inline-block mt-1">
                            {primaryAdmin.label} →
                          </Link>
                        )}
                        {guide.external && livePageUrl && !primaryAdmin && (
                          <a href={livePageUrl} target="_blank" rel="noreferrer" className="text-xs text-cyan-300/90 hover:underline inline-block mt-1">
                            Open live page to edit content ↗
                          </a>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {primaryAdmin ? (
                          <Link to={primaryAdmin.to}>
                            <Button size="sm" className="btn-gradient text-white border-0 w-full">Open editor</Button>
                          </Link>
                        ) : (
                          <Button size="sm" onClick={() => goFix(issue)} className="btn-gradient text-white border-0">
                            {guide.action}
                          </Button>
                        )}
                        {primaryAdmin && (
                          <Button size="sm" variant="ghost" className="text-white/60" onClick={() => goFix(issue)}>
                            See details
                          </Button>
                        )}
                      </div>
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          )}

          <GlassCard className="p-0 overflow-hidden">
            <div className="p-4">
              <SeoLivePreview
                path={page.path || "/"}
                siteOrigin={siteOrigin}
                liveData={liveSeo}
                draftSeo={{
                  meta_title: title,
                  meta_description: desc,
                  canonical_url: canonical,
                  robots: technical.robots?.configured || "index,follow",
                  og_title: og.title || "",
                  og_description: og.description || "",
                  og_image: og.image || "",
                }}
                fallbackTitle={page.name || ""}
                fallbackDescription=""
                showPageFrame={false}
              />
            </div>
          </GlassCard>

          <div className="flex flex-wrap gap-2 text-xs text-white/40">
            <span>Status: {page.publish_state || "draft"}</span>
            <span>·</span>
            <span>{page.source} / {page.page_type}</span>
            {technical.status ? <><span>·</span><span>HTTP {technical.status}</span></> : null}
          </div>
        </div>
      )}

      {tab === "Meta" && (
        <div className="space-y-4">
          {(rankingQuery || rankingPos || rankingPhrases.length > 0) && (
            <GlassCard className="p-4 space-y-2 border border-cyan-400/30 bg-cyan-400/5">
              <p className="text-sm text-white">
                {topicPhrases.length
                  ? "Google is showing this page for a topic search."
                  : brandOnly
                    ? "People searched your company name."
                    : "Google showed this page, but did not share the exact search."}
              </p>
              <p className="text-xs text-white/60">
                {rankingPos ? `Average position ${rankingPos}. ` : ""}
                {gscStats?.impressions ? `Shown ${gscStats.impressions} times. ` : ""}
                {gscStats?.clicks !== "" && gscStats?.clicks != null ? `Clicked ${gscStats.clicks} times. ` : ""}
                {topicPhrases.length
                  ? "Add one of the phrases below to the title, then Save and Publish."
                  : brandOnly
                    ? `Searches: ${rankingPhrases.slice(0, 4).join(", ")}. This page already appears for your company name. Rewriting the title will not create more clicks.`
                    : "The title below is what Google has now. Change it only if it does not describe the page, then Save and Publish."}
              </p>
              {topicPhrases.map((phrase) => (
                <Button key={phrase} size="sm" variant="ghost" className="text-cyan-100 mr-2 mb-1" onClick={() => { setSeo("title", titleWithPhrase(title, phrase)); setHighlight("title"); }}>
                  Add “{phrase}” to the title
                </Button>
              ))}
            </GlassCard>
          )}
          <SeoLivePreview
            path={page.path || "/"}
            siteOrigin={siteOrigin}
            liveData={liveSeo}
            draftSeo={{
              meta_title: title,
              meta_description: desc,
              canonical_url: canonical,
              robots: technical.robots?.configured || "index,follow",
              og_title: og.title || "",
              og_description: og.description || "",
              og_image: og.image || "",
              og_type: og.type || "website",
            }}
            fallbackTitle={page.name || live(seo.title) || ""}
            fallbackDescription={live(seo.description) || ""}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassCard className="p-5 space-y-5">
            <p className="text-xs text-white/45">What Google usually shows in search. Yellow ring = field that needs a fix.</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" className="text-white/70" disabled={aiBusy} onClick={() => runAi("generate_title")}>
                {aiBusy ? "…" : "Suggest title"}
              </Button>
              <Button size="sm" variant="ghost" className="text-white/70" disabled={aiBusy} onClick={() => runAi("generate_description")}>
                Suggest description
              </Button>
            </div>

            <div id="seo-field-title" className={fieldRing(highlight, "title")}>
              <Label className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-2">
                Title
                {highlight === "title" && <span className="normal-case text-amber-200 font-normal">← fix this</span>}
              </Label>
              <Input
                value={title}
                onChange={(e) => setSeo("title", e.target.value)}
                className={`bg-black/20 text-white mt-1 ${highlight === "title" ? "border-amber-400/70" : "border-white/10"}`}
                placeholder="Page title for Google"
              />
              <p className={`text-[11px] mt-1 ${title.length >= 40 && title.length <= 60 ? "text-emerald-300" : "text-amber-200/80"}`}>
                {title.length} characters · aim 40–60
              </p>
            </div>

            <div id="seo-field-description" className={fieldRing(highlight, "description")}>
              <Label className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-2">
                Description
                {highlight === "description" && <span className="normal-case text-amber-200 font-normal">← fix this</span>}
              </Label>
              <Textarea
                value={desc}
                onChange={(e) => setSeo("description", e.target.value)}
                rows={4}
                className={`bg-black/20 text-white mt-1 ${highlight === "description" ? "border-amber-400/70" : "border-white/10"}`}
                placeholder="One or two sentences about this page"
              />
              <p className={`text-[11px] mt-1 ${desc.length >= 120 && desc.length <= 160 ? "text-emerald-300" : "text-amber-200/80"}`}>
                {desc.length} characters · aim 120–160
              </p>
            </div>

            <details className="text-xs text-white/50" open={highlight === "canonical" || highlight === "indexing"}>
              <summary className="cursor-pointer hover:text-white/70">Canonical &amp; indexing (rarely needed)</summary>
              <div className="mt-3 space-y-3">
                <div id="seo-field-canonical" className={fieldRing(highlight, "canonical")}>
                  <Label className="text-xs uppercase tracking-wider text-white/50">Canonical</Label>
                  <select
                    className="mt-1 w-full h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2"
                    value={technical.canonical_mode || "automatic"}
                    onChange={(e) => onChange({ ...page, technical: { ...technical, canonical_mode: e.target.value } })}
                  >
                    <option value="automatic">Automatic</option>
                    <option value="self">Self</option>
                    <option value="custom">Custom</option>
                  </select>
                  <Input value={canonical} onChange={(e) => setTech("canonical", e.target.value)} className="bg-black/20 border-white/10 text-white mt-2" />
                </div>
                <div id="seo-field-indexing" className={fieldRing(highlight, "indexing")}>
                  <Label className="text-xs uppercase tracking-wider text-white/50">Indexing</Label>
                  <div className="flex gap-3 mt-2 text-sm text-white/70">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!(technical.robots?.configured || "").includes("noindex")}
                        onChange={(e) => setTech("robots", `${e.target.checked ? "index" : "noindex"},${(technical.robots?.configured || "index,follow").includes("nofollow") ? "nofollow" : "follow"}`)}
                      />
                      Index
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!(technical.robots?.configured || "").includes("nofollow")}
                        onChange={(e) => setTech("robots", `${(technical.robots?.configured || "").includes("noindex") ? "noindex" : "index"},${e.target.checked ? "follow" : "nofollow"}`)}
                      />
                      Follow
                    </label>
                  </div>
                </div>
              </div>
            </details>
          </GlassCard>

          <GlassCard className="p-5 space-y-4">
            <p className="text-[11px] uppercase tracking-wider text-white/40">Social share fields</p>
            <Input value={og.title || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, title: e.target.value } } })} placeholder="Share title" className="bg-black/20 border-white/10 text-white" />
            <Input value={og.description || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, description: e.target.value } } })} placeholder="Share description" className="bg-black/20 border-white/10 text-white" />
            <Input value={og.image || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, image: e.target.value } } })} placeholder="Share image URL (~1200×630)" className="bg-black/20 border-white/10 text-white" />
            <p className="text-xs text-white/40">Draft updates the preview above as you type. Save → Publish to change Live.</p>
          </GlassCard>
        </div>
        </div>
      )}

      {tab === "Content" && (
        <GlassCard id="seo-field-content" className={`p-5 text-sm text-white/70 space-y-3 ${fieldRing(highlight, "content")}`}>
          <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 space-y-3">
            <p className="text-amber-50 text-sm font-medium">Fix page copy in the admin content editor</p>
            <p className="text-xs text-amber-100/75 leading-relaxed">
              H1 and body text are managed outside SEO. Open the matching Brand &amp; Pages / CMS screen, save there, then come back and tap <strong className="font-medium text-amber-50">Check page</strong>.
            </p>
            <div className="flex flex-col gap-2">
              {adminLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 hover:bg-cyan-400/15"
                >
                  <span className="text-sm text-cyan-50 font-medium">{l.label} →</span>
                  {l.hint && <span className="block text-[11px] text-cyan-100/60 mt-0.5">{l.hint}</span>}
                </Link>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {livePageUrl && (
                <a href={livePageUrl} target="_blank" rel="noreferrer" className="text-xs text-white/50 hover:text-white/80 underline-offset-2 hover:underline">
                  Preview live page ↗
                </a>
              )}
              <button type="button" className="text-xs text-white/50 hover:text-white/80" onClick={() => selectTab("Overview")}>
                Back to Fix problems
              </button>
            </div>
          </div>
          <p>Words: {page.content?.word_count ?? "—"} · H1: {(page.content?.h1 || []).join(" | ") || "none found"}</p>
          <p>H2: {(page.content?.h2 || []).join(" · ") || "None detected"}</p>
          <p className="text-white/50">{page.content?.text_excerpt || "Run Check page to extract copy from the live URL."}</p>
          <Button variant="ghost" className="text-white/80" disabled={aiBusy} onClick={() => runAi("improve_content")}>
            {aiBusy ? "Getting suggestions…" : "Get content suggestions"}
          </Button>
        </GlassCard>
      )}

      {tab === "Keywords" && (
        <GlassCard id="seo-field-keywords" className={`p-5 space-y-3 ${fieldRing(highlight, "keywords")}`}>
          {highlight === "keywords" && (
            <p className="text-amber-200 text-xs">← Add a primary keyword below, then Save.</p>
          )}
          <p className="text-xs text-white/45">Tell us the main search phrase for this page.</p>
          {(page.keywords || []).map((k, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2">
              <Input
                value={k.keyword}
                onChange={(e) => {
                  const next = [...(page.keywords || [])];
                  next[i] = { ...k, keyword: e.target.value };
                  onChange({ ...page, keywords: next });
                }}
                className="bg-black/20 border-white/10 text-white"
                placeholder="e.g. modern business website"
              />
              <select
                value={k.type}
                onChange={(e) => {
                  const next = [...(page.keywords || [])];
                  next[i] = { ...k, type: e.target.value };
                  onChange({ ...page, keywords: next });
                }}
                className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2"
              >
                <option value="primary">primary</option>
                <option value="secondary">secondary</option>
                <option value="supporting">supporting</option>
              </select>
              <button className="text-white/40" onClick={() => onChange({ ...page, keywords: page.keywords.filter((_, idx) => idx !== i) })} aria-label="Remove keyword">
                <Icons.X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button
            variant="ghost"
            className="text-white/80"
            onClick={() => onChange({ ...page, keywords: [...(page.keywords || []), { keyword: "", type: (page.keywords || []).some((k) => k.type === "primary") ? "supporting" : "primary" }] })}
          >
            Add keyword
          </Button>
        </GlassCard>
      )}

      {tab === "Technical" && (
        <GlassCard id="seo-field-technical" className={`p-5 text-sm text-white/70 space-y-2 ${fieldRing(highlight, "technical")}`}>
          <p>HTTPS: {(page.url || "").startsWith("https") ? "Yes" : "No"}</p>
          <p>Redirect hops: {(technical.hops || []).map((h) => h.status).join(" → ") || "None"}</p>
          <p>Crawl error: {technical.crawl_error || "None"}</p>
          <p>Robots: {technical.robots?.configured || technical.robots?.published || "index,follow"}</p>
        </GlassCard>
      )}

      {tab === "Schema" && (
        <GlassCard id="seo-field-schema" className={`p-5 space-y-2 ${fieldRing(highlight, "schema")}`}>
          {highlight === "schema" && <p className="text-amber-200 text-xs">← Fix or add JSON-LD here, then Save → Publish.</p>}
          <Textarea
            rows={12}
            value={typeof page.schema === "object" ? (page.schema.configured || "") : (page.schema || "")}
            onChange={(e) => onChange({ ...page, schema: { ...(typeof page.schema === "object" ? page.schema : {}), configured: e.target.value } })}
            className="bg-black/40 border-white/10 text-white font-mono text-xs"
          />
          <div className="flex gap-2 flex-wrap">
            {["Organization", "WebPage", "Service", "FAQPage", "Article"].map((t) => (
              <Button key={t} size="sm" variant="ghost" className="text-white/70" disabled={aiBusy} onClick={() => runAi("generate_schema")}>{t} template</Button>
            ))}
            {onValidateSchema && (
              <Button
                size="sm"
                variant="ghost"
                className="text-white/70"
                onClick={async () => {
                  const raw = typeof page.schema === "object" ? (page.schema.configured || "") : (page.schema || "");
                  setSchemaResult(await onValidateSchema(raw));
                }}
              >
                Validate JSON-LD
              </Button>
            )}
          </div>
          {schemaResult && (
            <p className={`text-sm ${schemaResult.ok ? "text-emerald-300" : "text-amber-300"}`}>
              {schemaResult.ok ? "Valid JSON-LD" : (schemaResult.errors || []).join(" ")}
            </p>
          )}
        </GlassCard>
      )}

      {tab === "Social" && (
        <GlassCard id="seo-field-social" className={`p-5 space-y-3 ${fieldRing(highlight, "social")}`}>
          {highlight === "social" && <p className="text-amber-200 text-xs">← Fill title and image for link previews.</p>}
          <Input value={og.title || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, title: e.target.value } } })} placeholder="Share title" className="bg-black/20 border-white/10 text-white" />
          <Input value={og.description || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, description: e.target.value } } })} placeholder="Share description" className="bg-black/20 border-white/10 text-white" />
          <Input value={og.image || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, image: e.target.value } } })} placeholder="Image URL (1200×630)" className="bg-black/20 border-white/10 text-white" />
          <SeoLivePreview
            path={page.path || "/"}
            siteOrigin={siteOrigin}
            liveData={liveSeo}
            draftSeo={{
              meta_title: title,
              meta_description: desc,
              canonical_url: canonical,
              og_title: og.title || "",
              og_description: og.description || "",
              og_image: og.image || "",
            }}
            fallbackTitle={page.name || ""}
          />
        </GlassCard>
      )}

      {tab === "Links" && (
        <GlassCard id="seo-field-links" className={`p-5 text-sm text-white/70 space-y-2 ${fieldRing(highlight, "links")}`}>
          {highlight === "links" && (
            <p className="text-amber-200 text-xs">← Add internal links in the page content on the live site, then re-check.</p>
          )}
          <p>Internal: {(page.links?.internal || []).length} · Outbound: {(page.links?.outbound || []).length}</p>
          {(page.recommendations || []).map((r, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center border-b border-white/5 py-2">
              <span className="flex-1">{r.anchor} → {r.target_url} · {r.reason}</span>
              <button className="text-white/40" onClick={() => onChange({ ...page, recommendations: page.recommendations.filter((_, idx) => idx !== i) })}>Ignore</button>
              <button className="text-white/70" onClick={() => onLinkTask && onLinkTask(r)}>Task</button>
            </div>
          ))}
          {livePageUrl && (
            <a href={livePageUrl} target="_blank" rel="noreferrer" className="text-cyan-300 text-sm inline-block pt-2">Open live page ↗</a>
          )}
        </GlassCard>
      )}

      {tab === "Images" && (
        <GlassCard id="seo-field-images" className={`p-5 text-sm text-white/70 space-y-3 ${fieldRing(highlight, "images")}`}>
          {highlight === "images" && <p className="text-amber-200 text-xs">← Fill empty ALT fields, then Save ALT drafts.</p>}
          <p>Total {page.images?.total ?? 0} · Missing ALT {page.images?.missing_alt ?? 0}</p>
          {alts.map((img, i) => (
            <div key={img.src || i} className={`grid md:grid-cols-[1fr_2fr] gap-2 ${!(img.alt || "").trim() ? "rounded-md ring-1 ring-amber-400/40 p-2" : ""}`}>
              <span className="font-mono text-[11px] truncate text-white/40">{img.src}</span>
              <Input
                value={img.alt}
                onChange={(e) => setAlts((rows) => rows.map((row, idx) => (idx === i ? { ...row, alt: e.target.value } : row)))}
                className={`bg-black/20 text-white ${!(img.alt || "").trim() ? "border-amber-400/50" : "border-white/10"}`}
                placeholder="Describe this image"
              />
            </div>
          ))}
          {onSaveImages && <Button onClick={() => onSaveImages(alts)} className="btn-gradient text-white border-0">Save ALT drafts</Button>}
        </GlassCard>
      )}

      {tab === "History" && (
        <GlassCard className="p-5 text-sm text-white/60 space-y-2">
          {(page.history || []).length === 0 ? "No revisions yet." : page.history.map((h) => (
            <div key={h.id} className="flex justify-between border-b border-white/5 py-2 gap-2">
              <span>{h.field}</span>
              <span>{h.user_email} · {h.timestamp?.slice(0, 16)}</span>
              {onRestore && <button className="text-cyan-300" onClick={() => onRestore(h.id)}>Restore</button>}
            </div>
          ))}
        </GlassCard>
      )}

      {aiOut && (
        <GlassCard className="p-5 space-y-3 border border-cyan-500/20">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm text-white">Suggestions</h3>
              {aiOut.rationale && <p className="text-xs text-white/50 mt-1">{aiOut.rationale}</p>}
              {aiOut.note && <p className="text-xs text-amber-200/80 mt-1">{aiOut.note}</p>}
            </div>
            <button type="button" className="text-white/40 text-xs hover:text-white" onClick={() => { /* parent owns state — clear via empty */ }}>
              {/* keep visible until next action */}
            </button>
          </div>
          {typeof aiOut.suggestion === "string" && (
            <pre className="whitespace-pre-wrap text-sm text-white/80 bg-black/30 rounded-lg p-3 border border-white/5">{aiOut.suggestion}</pre>
          )}
          {Array.isArray(aiOut.suggestion) && (
            <ul className="space-y-2 text-sm text-white/80">
              {aiOut.suggestion.map((item, i) => (
                <li key={i} className="border-b border-white/5 pb-2">
                  {typeof item === "string" ? item : (item.fix || item.rule_id || JSON.stringify(item))}
                </li>
              ))}
            </ul>
          )}
          {aiOut.suggestion && typeof aiOut.suggestion === "object" && !Array.isArray(aiOut.suggestion) && (
            <pre className="whitespace-pre-wrap text-xs text-white/70 bg-black/30 rounded-lg p-3">{JSON.stringify(aiOut.suggestion, null, 2)}</pre>
          )}
          {typeof aiOut.suggestion === "string" && onApplyAi && (aiOut.action === "generate_title" || aiOut.action === "generate_description") && (
            <Button
              size="sm"
              className="btn-gradient text-white border-0"
              onClick={() => onApplyAi(
                aiOut.action === "generate_title"
                  ? { title: aiOut.suggestion }
                  : { description: aiOut.suggestion },
              )}
            >
              Apply to draft
            </Button>
          )}
        </GlassCard>
      )}
    </div>
  );
}
